import { useRef, useEffect, type ReactNode } from 'react';
import { useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import type { DraggableProps, DragItem, DropResult } from './types';

/**
 * Draggable wrapper component that makes its children draggable.
 * Supports both mouse and touch interactions.
 *
 * @example
 * ```tsx
 * <Draggable
 *   item={{ type: 'TILE', id: 'tile-1', data: { letter: 'A' } }}
 *   onDragStart={(item) => console.log('Started dragging', item)}
 *   onDragEnd={(item, result) => console.log('Dropped', result)}
 * >
 *   <div className="tile">A</div>
 * </Draggable>
 * ```
 */
export function Draggable({
  children,
  item,
  canDrag = () => true,
  onDragStart,
  onDragEnd,
  className = '',
  style,
  useCustomPreview = false,
  ariaLabel,
  testId,
}: DraggableProps): ReactNode {
  const ref = useRef<HTMLDivElement>(null);

  const [{ isDragging, canDragItem }, dragRef, previewRef] = useDrag<
    DragItem,
    DropResult,
    { isDragging: boolean; canDragItem: boolean }
  >(
    () => ({
      type: item.type,
      item: () => {
        onDragStart?.(item);
        return item;
      },
      canDrag: () => canDrag(),
      end: (draggedItem, monitor) => {
        const dropResult = monitor.getDropResult();
        onDragEnd?.(draggedItem, dropResult);
      },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
        canDragItem: monitor.canDrag(),
      }),
    }),
    [item, canDrag, onDragStart, onDragEnd]
  );

  // Connect the drag source to our ref
  useEffect(() => {
    if (ref.current) {
      dragRef(ref.current);
    }
  }, [dragRef]);

  // Use empty image as drag preview when custom preview is enabled
  useEffect(() => {
    if (useCustomPreview) {
      previewRef(getEmptyImage(), { captureDraggingState: true });
    }
  }, [useCustomPreview, previewRef]);

  // Build class names based on state
  const stateClasses = [
    isDragging ? 'opacity-50' : '',
    !canDragItem ? 'cursor-not-allowed opacity-60' : 'cursor-grab',
    isDragging ? 'cursor-grabbing' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      ref={ref}
      className={`${className} ${stateClasses}`.trim()}
      style={{ ...style, touchAction: 'none' }}
      role="button"
      tabIndex={canDragItem ? 0 : -1}
      aria-label={ariaLabel || `Draggable item ${item.id}`}
      aria-grabbed={isDragging}
      aria-disabled={!canDragItem}
      data-testid={testId}
      data-dragging={isDragging}
      data-can-drag={canDragItem}
    >
      {children}
    </div>
  );
}
